import axios, { AxiosError } from 'axios';
import {
  SectionItem,
  CreateItemData,
  UpdateItemData,
  OrderData,
  Language,
  ApiResponse,
} from '../types/pageBuilder';

const API_BASE_URL = 'https://patrixglobal.com/backend/api';

/**
 * Service for managing page section items
 */
class ItemsService {
  private token: string | null;

  constructor() {
    this.token = localStorage.getItem('admin_token');
  }

  /**
   * Get auth headers for API requests
   */
  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
    };
  }

  /**
   * Get all items for a section
   */
  async getItems(sectionId: number, language?: Language): Promise<SectionItem[]> {
    try {
      const params = new URLSearchParams({ section_id: sectionId.toString() });
      if (language) {
        params.append('language', language);
      }

      const response = await axios.get<ApiResponse<SectionItem[]>>(
        `${API_BASE_URL}/admin/pages/items?${params.toString()}`,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch items');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to fetch items');
      }
      throw error;
    }
  }

  /**
   * Create a new item
   */
  async createItem(itemData: CreateItemData): Promise<SectionItem> {
    try {
      const response = await axios.post<ApiResponse<SectionItem>>(
        `${API_BASE_URL}/admin/pages/items`,
        itemData,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to create item');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to create item');
      }
      throw error;
    }
  }

  /**
   * Update an existing item
   */
  async updateItem(itemId: number, itemData: UpdateItemData): Promise<void> {
    try {
      const response = await axios.put<ApiResponse<void>>(
        `${API_BASE_URL}/admin/pages/items/${itemId}`,
        itemData,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update item');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to update item');
      }
      throw error;
    }
  }

  /**
   * Delete an item
   */
  async deleteItem(itemId: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${API_BASE_URL}/admin/pages/items/${itemId}`,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete item');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to delete item');
      }
      throw error;
    }
  }

  /**
   * Reorder items within a section
   */
  async reorderItems(sectionId: number, orderData: OrderData[]): Promise<void> {
    try {
      const response = await axios.post<ApiResponse<void>>(
        `${API_BASE_URL}/admin/pages/items/reorder`,
        {
          section_id: sectionId,
          items: orderData,
        },
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to reorder items');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to reorder items');
      }
      throw error;
    }
  }

  /**
   * Duplicate an item
   */
  async duplicateItem(itemId: number): Promise<SectionItem> {
    try {
      const response = await axios.post<ApiResponse<{ new_item_id: number }>>(
        `${API_BASE_URL}/admin/pages/items/duplicate/${itemId}`,
        {},
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        // Fetch the newly created item
        const newItemId = response.data.data.new_item_id;
        // For now, we'll need to refetch items to get the new one
        // In a real implementation, the backend could return the full item
        throw new Error('Duplicate successful. Please refresh to see the new item.');
      }

      throw new Error(response.data.message || 'Failed to duplicate item');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to duplicate item');
      }
      throw error;
    }
  }
}

// Export a singleton instance
export const itemsService = new ItemsService();
